import { For, JSX, Match, Show, Switch, createEffect, createMemo, createSignal } from 'solid-js'
import { toast } from 'common'
import { StaffCandidate, visitorCheckin as api } from 'st-app/api'
import { requireVisitorCheckinContext } from '../VisitorCheckinContext'
import { requireStepContext } from '../../st-wizard/st-wizard'
import { Loader, Button } from 'st-app';
import { formatAddress, formatDob, formatGender as formatSex, formatHeight, formatName } from './util'
import { DenialScriptOverlay } from './denial-script-overlay'
import styles from './verification-step.module.css'

const NO_FLY_REASONS = [
    { code: '0', label: 'Custody' },
    { code: '1', label: 'Trespassing' },
    { code: '2', label: 'Past Incident' },
    { code: '3', label: 'Other' },
]

/**
 * Which panel a live re-screening sends staff to when they open a flagged row via [Review].
 * 'unavailable' is a real state, not a toast: screening could not be re-derived, so the visitor
 * cannot be cleared and the Print step must stay unreachable.
 */
type ReviewRoute = 'offender' | 'watchlist' | 'clean' | 'unavailable';

/**
 * Which panel the AMBIGUOUS STAFF BADGE resolution shows. Separate from ReviewRoute above because a
 * staff row is not screened at all, so none of that type's states apply to it.
 *
 * 'unavailable' is again a real state rather than a toast: with no candidate list there is no way to
 * say WHICH employee scanned in, and putting the wrong person on an emergency roster is the exact
 * failure this whole flow exists to prevent.
 */
type StaffRoute = 'loading' | 'choose' | 'unavailable';

/**
 * VISITOR_TYPE lookup code 3 = Staff. Same code print-step's VisitorTypeLookup labels 'Staff' and
 * VisitorScreeningService calls VISITOR_TYPE_STAFF; that C# constant is `internal` to
 * K12.VisitorManagement.VisitorCheckInCheckOut, so it never crosses this boundary as a value and the
 * literal is restated here.
 */
const VISITOR_TYPE_STAFF = '3';

const bannerStyle = (color?: string) => {
    switch ((color || 'Red').toLowerCase()) {
        case 'green':  return { bg: '#e7f4e8', border: '#1f8b3b', icon: 'fas fa-star',                    fg: '#14571f' };
        case 'yellow': return { bg: '#fdf6e3', border: '#c79100', icon: 'fas fa-triangle-exclamation',    fg: '#7a5b00' };
        default:       return { bg: '#fde8e8', border: '#c0341d', icon: 'fas fa-ban',                     fg: '#7a1d12' }; // Red
    }
};

export const VerificationStep = () => {
    const checkin = requireVisitorCheckinContext();
    const step = requireStepContext();

    const [isProcessing, setIsProcessing] = createSignal(false);

    // Read-aloud district script shown after a deny. null = not denied yet.
    const [denialScript, setDenialScript] = createSignal<string | null>(null);

    // Terminal close for the whole check-in modal, once the clerk taps Done on
    // the script overlay. Kept in DEV so the modal stays open for inspection.
    const finishAndClose = () => {
        if (import.meta.env.DEV) {
            toast.notify('notice', '(DEV mode, modal not closed)');
            setDenialScript(null);
            return;
        }
        if (window.ST) {
            window.ST.CloseModal();
            window.ST.RefreshView();
        }
    }

    // No-Fly modal state
    const [showNoFlyModal, setShowNoFlyModal] = createSignal(false);
    const [noFlyReason, setNoFlyReason] = createSignal('0');
    const [noFlyNotes, setNoFlyNotes] = createSignal('');
    const [noFlyDistrictWide, setNoFlyDistrictWide] = createSignal(true);
    const [isAddingToNoFly, setIsAddingToNoFly] = createSignal(false);

    const openNoFlyModal = () => {
        setNoFlyReason('0');
        setNoFlyNotes('');
        setNoFlyDistrictWide(true);
        setShowNoFlyModal(true);
    }

    const confirmAddToNoFly = async () => {
        if (isAddingToNoFly()) return;
        setIsAddingToNoFly(true);
        try {
            // Also a resolve path in review mode (it ends in DenyVisitor), so it takes the same
            // already-resolved-by-someone-else pre-check as the other resolve paths. Called directly
            // on checkin (not via the local claimReviewRow wrapper) because this dialog must react
            // differently to the two failure modes: 'already-resolved' closes the dialog along with
            // the whole wizard, but 'check-failed' means nothing was saved and the operator's
            // reason/notes must stay on screen so they can retry — closing the dialog there would
            // silently discard their input.
            const claim = await checkin.claimReviewRow();
            if (claim === 'already-resolved') {
                setIsAddingToNoFly(false);
                setShowNoFlyModal(false);
                showAlreadyResolvedAndClose();
                return;
            }
            if (claim === 'check-failed') {
                setIsAddingToNoFly(false);
                toast.notify('error', 'Could not confirm this row is still awaiting review — nothing was saved. Check your connection and try again.');
                return;
            }
            const result = await api.AddToNoFly(checkin.vmGU()!, noFlyReason(), noFlyNotes(), noFlyDistrictWide(), []);
            if (!result.ok && !result.alreadyExists) {
                // Failed — keep the dialog open so staff can retry.
                toast.notify('error', result.message ?? 'Could not add to the Blocked / No-Fly list.');
                setIsAddingToNoFly(false);
                return;
            }

            // Added (or already on the list): this visit is now blocked — deny it so the grid reflects it.
            try { await api.DenyVisitor(checkin.vmGU()!, 'NOFLY'); } catch (e) { console.error(e); }
            toast.notify('success', result.alreadyExists
                ? 'This adult is already on the Blocked / No-Fly list — entry denied.'
                : 'Visitor added to the Blocked / No-Fly list and denied entry.');

            setShowNoFlyModal(false);
            if (import.meta.env.DEV) {
                toast.notify('notice', '(DEV mode, modal not closed)');
            } else if (window.ST) {
                window.ST.CloseModal();
                window.ST.RefreshView();
            }
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while adding to the No-Fly list.');
        }
        setIsAddingToNoFly(false);
    }

    // Which panel the live re-screening decided staff need to see. null = not decided yet
    // (still running, or not review mode at all).
    const [reviewRoute, setReviewRoute] = createSignal<ReviewRoute | null>(null);
    const [rerunning, setRerunning] = createSignal(false);
    const isReview = () => checkin.entryMode() === 'review';

    /**
     * A staff row: a badge scan that matched MORE THAN ONE employee, stored REVW by the kiosk so
     * somebody can say which of the duplicates it actually was. EPC_STAFF.BADGE_NUM has a non-unique
     * index and this database holds 13 genuine duplicates, so the kiosk refuses to guess.
     *
     * Gated on review mode as well as the type. Only a row the server hydrated from an existing REVW
     * record can be resolved this way; an operator who merely picks "Staff" from the Visitor Type
     * dropdown mid-wizard on an ordinary walk-in is NOT this, and must keep the normal screening flow.
     *
     * Keyed on reviewVisitorType() — the type the ROW arrived with, captured immutably in
     * hydrateForReview — and NOT on the editable visitorType() signal the Print step's <Select> binds
     * to. This is a routing decision and it must not be reachable from the UI. With the editable
     * signal, an operator on a screening-flagged VISITOR row who set Visitor Type = 'Staff' on the
     * Print step and pressed Previous flipped this test: the visitor <Switch> and the No-Fly button
     * unmounted, and the staff picker rendered permanently stuck at staffRoute() === 'loading',
     * because the entry effect's entryScreeningStarted latch had already fired so loadStaffCandidates
     * could never run. That stranded the operator with no way forward.
     *
     * The operator stays free to edit visitorType() for the row's own data — that value is what gets
     * saved, and nothing here reads it.
     */
    const isStaffRow = () => isReview() && checkin.reviewVisitorType() === VISITOR_TYPE_STAFF;

    const [staffRoute, setStaffRoute] = createSignal<StaffRoute>('loading');
    const [staffCandidates, setStaffCandidates] = createSignal<StaffCandidate[]>([]);
    const [chosenStaffGU, setChosenStaffGU] = createSignal('');
    const [staffMessage, setStaffMessage] = createSignal('');

    const chosenStaffName = createMemo(() => {
        const gu = chosenStaffGU();
        const match = staffCandidates().find(c => c.staffGU === gu);
        return match?.formattedName || 'this employee';
    });

    /**
     * Load the employees this row's badge matches. Runs on entry and on every Try Again press.
     *
     * The candidate list is re-resolved SERVER-SIDE from the row's own stored badge value
     * (VisitorCheckinApi.GetStaffCandidatesForReview); this client never sends a candidate list and
     * never sends a badge. It only ever sends the row's GU, and the chosen GU is re-validated against
     * a fresh server-side resolution again at completion time.
     *
     * No screening call of any kind is made here — not checkin.verify(), not MatchBlockedVisitorDetail,
     * not VerifySexOffender. Staff bypass screening by design, and verify()'s VerifyBlockedVisitor
     * writes status durably, so merely opening a staff row must not touch it.
     */
    const loadStaffCandidates = async () => {
        if (rerunning()) return;

        setRerunning(true);
        setStaffRoute('loading');
        setStaffMessage('');

        // Fail closed while we do not have a list. verificationStatus stays 'pending', which
        // ensureVerified and submit() both refuse, so the Print step stays unreachable and no
        // CompleteCheckin can fire. Completion on this path goes exclusively through
        // CompleteStaffCheckIn below.
        checkin.setVerificationStatus('pending');
        checkin.setVerificationStatusMessage('');

        try {
            const candidates = await api.GetStaffCandidatesForReview(checkin.vmGU()!);

            // A candidate with no staffGU cannot be identified, and an unidentifiable row defeats the
            // emergency accountability this exists for — the server's ChooseStaffOutcome discards them
            // for the same reason, so they are discarded here too rather than rendered as a dead row.
            const usable = (candidates ?? []).filter(c => !!c?.staffGU);

            if (usable.length === 0) {
                setStaffCandidates([]);
                setChosenStaffGU('');

                // AN EMPTY LIST DOES NOT MEAN "THE BADGE MATCHES NOBODY". All four of
                // GetStaffCandidatesForReview's server-side refusals arrive here as an empty list and
                // are indistinguishable from each other: the row is gone, it is not a staff row, it
                // belongs to another organization, or it is no longer REVW. The last of those is by far
                // the likeliest — another operator resolved the row first — so this used to send staff
                // off to edit employee badge numbers in Staff maintenance for a row that was simply
                // already done.
                //
                // Probe for that case with the SAME shared mechanism every other resolve path uses
                // (VisitorCheckinContext.claimReviewRow → IsAwaitingReview), and take the SAME
                // showAlreadyResolvedAndClose path the visitor resolutions take. Called directly on
                // checkin rather than through the local claimReviewRow wrapper because that wrapper's
                // 'check-failed' branch says "nothing was saved", which belongs to a write — this is a
                // read, and nothing was being saved either way.
                const claim = await checkin.claimReviewRow();
                if (claim === 'already-resolved') {
                    setStaffMessage('This staff check-in was already resolved by another user.');
                    setStaffRoute('unavailable');
                    showAlreadyResolvedAndClose();
                    return;
                }

                // Still REVW, or the probe itself failed: we genuinely do not know why the list came
                // back empty, so assert NO cause. Naming one we cannot verify is what sent staff to the
                // wrong screen. Fail closed and offer the retry, exactly as before.
                setStaffMessage('No matching employees came back for this badge, so there is nobody to check '
                    + 'in yet. Nothing has been changed. Try again — if it keeps happening, this row may need '
                    + 'an administrator to look at it.');
                setStaffRoute('unavailable');
                return;
            }

            setStaffCandidates(usable);
            // Pre-select when the ambiguity has resolved itself down to one person (a duplicate badge
            // was corrected in staff records since the scan). Still requires the explicit Complete
            // press — nothing is written just because the list arrived.
            setChosenStaffGU(usable.length === 1 ? usable[0].staffGU! : '');
            setStaffRoute('choose');
        } catch (e) {
            console.error(e);
            setStaffCandidates([]);
            setChosenStaffGU('');
            setStaffMessage('The list of matching employees could not be loaded, so this check-in cannot be '
                + 'completed yet. Nothing has been changed.');
            setStaffRoute('unavailable');
        } finally {
            setRerunning(false);
        }
    };

    /**
     * Re-derive the screening verdict for a row the kiosk flagged, and route to the panel that
     * lets staff resolve it. Runs on entry and on every Retry press.
     *
     * Deliberately does NOT call checkin.verify(): that runs VerifyBlockedVisitor, which writes
     * status durably — ApproveVisitor -> ALLOW on a watch-list NOTIFY disposition, DenyVisitor on
     * a hard block — and notifies staff. Merely OPENING this wizard would therefore resolve the
     * row before anyone decided anything, and re-notify on every single open. The match-only
     * MatchBlockedVisitorDetail has none of those side effects; the notification comes from the
     * resolution the operator picks (ConfirmBlockedDeny / ConfirmWatchAllow) instead.
     *
     * VerifySexOffender is re-run as-is: it has no durable side effects, and re-running it is
     * exactly what repopulates the offender comparison panel, which the [Review] payload
     * deliberately does not carry.
     */
    const runReviewScreening = async () => {
        if (rerunning()) return;

        // Fail closed. 'error' is refused by both submit() and ensureVerified(), and the step is
        // never marked completed, so no path from here reaches the Print step. It must be set on
        // every 'unavailable' exit — leaving the status at 'pending' would keep the verification
        // spinner on screen underneath the retry panel.
        const failClosed = () => {
            checkin.setOffenders([]);
            checkin.setBlockedMatches([]);
            checkin.setVerificationStatus('error');
            checkin.setVerificationStatusMessage('Screening could not be completed.');
            setReviewRoute('unavailable');
        };

        setRerunning(true);
        checkin.setVerificationStatus('pending');
        checkin.setVerificationStatusMessage('Re-checking this visitor against screening...');

        try {
            // ONE watch-list round trip. MatchBlockedVisitorDetail already carries the tier
            // (BlockedVisitorResult.Tier, set from the same MatchTier the tier-only call returned),
            // so asking for the tier separately would just run the identical match twice.
            const detail = await api.MatchBlockedVisitorDetail(checkin.scan()!, checkin.vmStationGU()!);
            const tier: string = detail?.tier ?? 'None';
            const offenderResult: any = await api.VerifySexOffender(checkin.scan()!);
            const offenders = offenderResult?.matchingOffenders ?? [];

            if (offenderResult?.passed !== true) {
                // VerifySexOffender swallows its own exceptions and returns Passed=false with a
                // null offender list, so "did not pass but produced nothing to show" means the
                // registry could not be reached — it is NOT a match. Fail closed instead of
                // rendering an empty comparison panel that staff could never resolve.
                if (offenders.length === 0) {
                    failClosed();
                    return;
                }

                checkin.setBlockedMatches([]);
                checkin.setOffenders(offenders);
                checkin.setVerificationStatus('dangerous');
                checkin.setVerificationStatusMessage('Scanned ID failed sex offender verification test.');
                setReviewRoute('offender');
                return;
            }

            // Tier is the string name ("None" | "Warn" | "Block"), matching every other tier value
            // that crosses this boundary — the C# enum itself would serialize as a number.
            if (tier !== 'None') {
                const matches = detail?.matches ?? [];
                if (matches.length === 0) {
                    // The tier said there is a match but no rows came back, so there is nothing to
                    // show staff. Do not silently clear the visitor.
                    failClosed();
                    return;
                }

                checkin.setOffenders([]);
                checkin.setBlockedMatches(matches);
                if (tier === 'Block') {
                    checkin.setVerificationStatus('dangerous');
                    checkin.setVerificationStatusMessage(detail?.message || 'Visitor is on the blocked list.');
                } else {
                    checkin.setVerificationStatus('review');
                    checkin.setVerificationStatusMessage('Possible match on the blocked / No-Fly list — staff review required.');
                }
                setReviewRoute('watchlist');
                return;
            }

            // Nothing matches any more. Clearing the row still requires staff to finish the
            // wizard, which is what records ALLOW via CompleteCheckin.
            checkin.setOffenders([]);
            checkin.setBlockedMatches([]);
            checkin.setVerificationStatus('verified');
            checkin.setVerificationStatusMessage('Screening re-run — no matches found.');
            setReviewRoute('clean');
        } catch (e) {
            console.error(e);
            failClosed();
        } finally {
            setRerunning(false);
        }
    };

    // Entry screening, fired exactly once, as soon as we know WHICH kind of entry this is.
    //
    // An unset entryMode is "not ready yet", not "not review": the [Review] payload is pushed
    // through ST data binding and can land after mount, and letting the durable verify() run in
    // that window would resolve the row before anyone decided anything. But bailing out of a
    // plain onMount would never re-arm — if it ever did fire early the screen would be a
    // permanent spinner. So this tracks entryMode() and re-runs when it becomes set, with a
    // once-latch, the same pattern the hydration effect in st-visitor-checkin.tsx uses.
    let entryScreeningStarted = false;
    createEffect(() => {
        const mode = checkin.entryMode();
        if (entryScreeningStarted || !mode) return;

        entryScreeningStarted = true;

        // In review mode the kiosk already captured this visitor, and verify() would have durable
        // side effects (see runReviewScreening) — re-derive the verdict side-effect-free instead.
        if (mode === 'review') {
            // A STAFF row is not screened, re-derived or otherwise: staff bypass the offender and
            // watch-list checks by design, so the only open question is WHICH employee the duplicate
            // badge belongs to. Route to the candidate picker and run no screening at all.
            //
            // isStaffRow() reads reviewVisitorType() here rather than tracking it, and that is safe
            // for the same reason runReviewScreening can already rely on checkin.scan() being
            // populated: hydrateForReview sets the captured type, entryMode and the scan in one
            // synchronous call, and it writes the captured type FIRST — so even unbatched this effect
            // cannot observe mode 'review' before the type it routes on. Reading the IMMUTABLE type is
            // also what makes this latched, once-only effect safe: the value it decided on can no
            // longer change underneath it, so there is no state in which the route says "staff" but
            // loadStaffCandidates was never allowed to run.
            if (isStaffRow()) {
                void loadStaffCandidates();
                return;
            }
            void runReviewScreening();
            return;
        }

        if (checkin.verificationStatus() === 'pending' && !checkin.isVerifying()) {
            checkin.verify();
        }
    })

    const [graceDone, setGraceDone] = createSignal(false);
    createEffect(() => {
        if (checkin.verificationStatus() === 'verified' && !offender()) {
            if (checkin.repeatTier() === 'Grace') {
                if (!graceDone()) {
                    setGraceDone(true);
                    (async () => {
                        try { await api.ReopenVisit(checkin.repeatPriorVisitGU()); }
                        catch (e) { console.error(e); }
                        if (import.meta.env.DEV) {
                            toast.notify('success', 'Grace re-entry (DEV mode, modal not closed)');
                        } else {
                            setTimeout(() => { if (window.ST) { window.ST.CloseModal(); window.ST.RefreshView(); } }, 1500);
                        }
                    })();
                }
                return;
            }
            if (!step.completed()) {
                setTimeout(() => { step.setCompleted(true); step.next(); }, 1000);
            }
        }
    })

    const offender = createMemo(() => checkin.offenders()?.at(0))

    /**
     * UI wrapper over the shared context pre-check (VisitorCheckinContext.claimReviewRow). Every
     * resolve path asks the server whether the row is still awaiting resolution before writing
     * anything; outside review mode the context short-circuits to 'ok' with no round trip.
     *
     * Both failure modes write nothing, but they are reported DIFFERENTLY on purpose:
     *
     * - 'already-resolved' — the server answered, and somebody else's decision is now the truth.
     *   Say so, close the wizard, refresh the grid.
     * - 'check-failed' — the CHECK itself failed (network / server error). We do not know the
     *   row's state, so we still refuse to write, but the operator's work stays on screen and the
     *   message says nothing was saved and to retry. Reporting this as "already resolved by
     *   another user" would be a lie AND would throw away everything they had entered.
     */
    const claimReviewRow = async (): Promise<boolean> => {
        const result = await checkin.claimReviewRow();
        if (result === 'ok') return true;

        if (result === 'already-resolved') {
            showAlreadyResolvedAndClose();
            return false;
        }

        toast.notify('error', 'Could not confirm this row is still awaiting review — nothing was saved. Check your connection and try again.');
        return false;
    }

    const showAlreadyResolvedAndClose = () => {
        toast.notify('notice', 'This visitor was already resolved by another user.');
        if (import.meta.env.DEV) {
            toast.notify('notice', '(DEV mode, modal not closed)');
            return;
        }
        if (window.ST) {
            window.ST.CloseModal();
            window.ST.RefreshView();
        }
    }

    /**
     * REVIEW-MODE APPROVAL IS NOT WRITTEN HERE.
     *
     * An approve resolution (False Alarm / Not a Match / Allow + Notify) only claims the row and
     * advances to the Print step. The single durable write — CompleteCheckin — happens in the
     * wizard's submit() (st-visitor-checkin.tsx), behind the same pre-check, because the Print
     * step is where staff actually edit visitor type, reason and meeting-with. Writing here would
     * persist the PRE-edit values, and submit() would then write a second time.
     *
     * Consequence, deliberately chosen: a staff member who clicks an approve button and then
     * abandons the wizard at the Print step leaves the row at REVW — still flagged, still
     * resolvable by anyone. That is strictly better than the alternative it replaces, which left
     * the row ALLOW with a stamped CheckInDatetime and whatever unreviewed values the kiosk
     * captured, with nothing on the grid to show it had never been finished.
     *
     * Deny is different and unchanged: it is terminal, has no Print step, and writes immediately.
     */
    const writeApprovalIfNotReview = async () => {
        // Non-review (walk-in) behaviour is byte-identical to before: ApproveVisitor, right here,
        // awaited before the caller marks the step verified/completed — so a failed write still
        // throws into the caller's catch and leaves the status untouched.
        if (!isReview()) await api.ApproveVisitor(checkin.vmGU()!);
    }

    const confirmMatch = async () => {
        if (isProcessing()) return;

        setIsProcessing(true);
        try {
            if (!await claimReviewRow()) { setIsProcessing(false); return; }
            const resp = await api.DenyVisitor(checkin.vmGU()!, 'OFFENDER');
            // Deny recorded + notifications sent server-side; show the district
            // script for the clerk to read aloud, then finishAndClose on Done.
            setDenialScript(resp?.message || 'This visitor has been denied entry.');
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while denying the visitor.');
        }
        setIsProcessing(false);
    }

    const rejectMatch = async () => {
        if (isProcessing()) return;

        setIsProcessing(true);
        try {
            if (!await claimReviewRow()) { setIsProcessing(false); return; }
            await writeApprovalIfNotReview();
            checkin.setVerificationStatus('verified');
            step.setCompleted(true);
            step.next();
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while clearing the visitor.');
        }
        setIsProcessing(false);
    }

    const notAMatch = async () => {
        if (isProcessing()) return;

        setIsProcessing(true);
        try {
            if (!await claimReviewRow()) { setIsProcessing(false); return; }
            await writeApprovalIfNotReview();
            checkin.setVerificationStatus('verified');
            checkin.setVerificationStatusMessage('Verification Complete');
            step.setCompleted(true);
            step.next();
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while clearing the visitor.');
        }
        setIsProcessing(false);
    }

    const confirmBlocked = async () => {
        if (isProcessing()) return;

        setIsProcessing(true);
        try {
            if (!await claimReviewRow()) { setIsProcessing(false); return; }
            const match = checkin.blockedMatches()[0];
            const resp: any = await api.ConfirmBlockedDeny(checkin.vmGU()!, match?.vmBlockedVisitorGU!);
            // Deny recorded; show the district script to read aloud. Fall back to
            // the Block-tier message already loaded during verify() if the deny
            // response carries none.
            const script = (resp?.message as string) || checkin.verificationStatusMessage() || '';
            setDenialScript(script || 'This visitor has been denied entry.');
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while denying the visitor.');
        }
        setIsProcessing(false);
    }

    const allowAndNotify = async () => {
        if (isProcessing()) return;
        setIsProcessing(true);
        try {
            if (!await claimReviewRow()) { setIsProcessing(false); return; }
            const entry = checkin.blockedMatches()[0];
            await api.ConfirmWatchAllow(checkin.vmGU()!, entry?.vmBlockedVisitorGU!);
            toast.notify('success', 'Visitor allowed. Designated staff have been notified.');
            checkin.setVerificationStatus('verified');
            step.setCompleted(true);
            step.next();
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while allowing the visitor.');
        }
        setIsProcessing(false);
    };

    /**
     * Resolve an ambiguous staff badge as the chosen employee, and finish. TERMINAL: unlike every
     * visitor resolution above, this does not advance to the Print step.
     *
     * NOTHING IS PRINTED. A staff check-in is recorded for emergency accountability only, so there is
     * no badge, no printer and no Print step on this path — checkin.print() is never called, and
     * neither is api.CompleteCheckin(): the whole durable write is CompleteStaffCheckIn, which sets
     * ALLOW, stores the chosen employee's first and last name separately, and closes that person's
     * other open staff visits so the roster shows them at one building only.
     *
     * NOTHING IS SCREENED either, here or on entry. Staff bypass the offender and watch-list checks by
     * design, and verify()'s VerifyBlockedVisitor writes status durably, so it must never run on a
     * staff row.
     *
     * The chosen staffGU is re-validated SERVER-SIDE against a fresh resolution of the row's own
     * stored badge value, so this radio group cannot be used to check in an arbitrary person.
     */
    const completeStaffCheckIn = async () => {
        if (isProcessing()) return;

        const staffGU = chosenStaffGU();
        if (!staffGU) return;

        setIsProcessing(true);
        try {
            // The same shared pre-check every visitor resolve path runs, with the same two failure
            // messages. It is NOT the real guard — CompleteStaffCheckIn re-reads the row server-side in
            // the same call that writes and refuses anything that is no longer REVW. This one only
            // exists so the operator gets the honest "somebody beat you to it" / "nothing was saved"
            // wording instead of a bare failure.
            if (!await claimReviewRow()) { setIsProcessing(false); return; }

            const completed = await api.CompleteStaffCheckIn(checkin.vmGU()!, staffGU);
            if (!completed) {
                // Every server refusal is fail-closed and wrote NOTHING: the row is no longer REVW, is
                // not in this focus organization, or the chosen employee is no longer among the people
                // the badge resolves to. We cannot tell which from a boolean, so say all of it honestly
                // and reload the list so the operator sees current truth rather than a stale list.
                toast.notify('error', 'This staff check-in could not be completed and nothing was saved. '
                    + 'The row may have just been resolved by someone else, or the badge may no longer match '
                    + 'this employee. The list of matching employees has been refreshed.');
                void loadStaffCandidates();
                setIsProcessing(false);
                return;
            }

            toast.notify('success', `Checked in ${chosenStaffName()}. No badge is printed for staff.`);
            if (import.meta.env.DEV) {
                toast.notify('notice', '(DEV mode, modal not closed)');
            } else if (window.ST) {
                window.ST.CloseModal();
                window.ST.RefreshView();
            }
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while completing the staff check-in. Nothing was saved.');
        }
        setIsProcessing(false);
    };

    return (
        <div class={styles['verification-step']}>
            {/*
              * STAFF REVIEW ROW — the candidate picker. Rendered INSTEAD of the screening panels below,
              * never alongside them.
              *
              * A staff REVW row means a genuinely ambiguous badge: nobody has decided who this is, the
              * row carries no PersonGU, and staff must pick. A WATCH-LIST hit does NOT arrive here -
              * as of 2026-08-06 staff are screened, and a hit is DENIED outright (Status=DENY,
              * DenyReason=NOFLY) rather than being sent to review, so it never opens this wizard.
              *
              * The banner still branches on candidate count rather than hardcoding the plural claim.
              * One candidate is not supposed to happen on this path, but it is reachable if the badge's
              * duplicates changed between the scan and the review - and telling somebody "this badge
              * matched more than one employee" above a list of exactly one name is visibly wrong, which
              * is worse than a vaguer sentence.
              *
              * NOTE the comment that used to sit here claimed "a staff row is never screened, so
              * offenders() and blockedMatches() stay empty". Staff ARE screened now; what remains true
              * is that a screened-out employee is denied at the kiosk and never reaches this panel.
              */}
            <Show when={isStaffRow()}>
                <div style='display:flex; flex-direction:column; align-items:stretch; gap:16px; width:100%; padding:8px 0;'>
                    <div style='display:flex; gap:12px; align-items:flex-start; padding:12px 16px; border-radius:6px; background:#fdf6e3; border:1px solid #c79100; color:#7a5b00;'>
                        <i class='fas fa-id-badge' style='font-size:1.4em; margin-top:2px;' />
                        <Show
                            when={staffCandidates().length > 1}
                            fallback={
                                <span>
                                    <strong>Confirm who scanned in.</strong>{' '}
                                    This badge resolves to a single employee now, though it was recorded as
                                    needing review. Staff bypass visitor screening, and no badge is printed.
                                </span>
                            }
                        >
                            <span>
                                <strong>This badge matched more than one employee.</strong>{' '}
                                Choose who actually scanned in. Staff bypass visitor screening, and no badge is printed.
                            </span>
                        </Show>
                    </div>

                    <Show when={checkin.scan()?.AAMVA?.idNumber}>
                        <div class={styles['field']}>
                            <span class={styles['label']}>Badge Scanned:</span>
                            <span class={styles['value']}>{checkin.scan()?.AAMVA?.idNumber}</span>
                        </div>
                    </Show>

                    <Switch>
                        <Match when={staffRoute() === 'loading'}>
                            <div style='display:flex; flex-direction:column; align-items:center; gap:8px; padding:24px;'>
                                <Loader />
                                <span>Looking up the employees this badge matches...</span>
                            </div>
                        </Match>

                        {/*
                          * Fail closed. No candidates means there is no way to say WHICH employee this is,
                          * and there is deliberately no "check in anyway" escape: guessing would put the
                          * wrong person on an emergency roster, which is the failure this flow exists to
                          * prevent. The row stays REVW and stays resolvable. Retry only, no limit.
                          */}
                        <Match when={staffRoute() === 'unavailable'}>
                            <div class={styles['screening-unavailable']}>
                                <i class='fas fa-triangle-exclamation' />
                                <p>{staffMessage()}</p>
                                <Button icon='fas fa-rotate-right' type='primary' enabled={!rerunning()} loading={rerunning()} onClick={() => void loadStaffCandidates()}>
                                    {rerunning() ? 'Retrying...' : 'Try again'}
                                </Button>
                            </div>
                        </Match>

                        <Match when={staffRoute() === 'choose'}>
                            <span class={styles['comparison-panel-title']}>Which employee is this?</span>
                            <div style='display:flex; flex-direction:column; gap:8px;' role='radiogroup' aria-label='Matching employees'>
                                <For each={staffCandidates()}>{(candidate) => (
                                    <label style='display:flex; align-items:center; gap:12px; padding:12px 16px; border:1px solid var(--st-panel-border); border-radius:6px; background:var(--st-well-bg); cursor:pointer;'>
                                        <input
                                            type='radio'
                                            name='vm-staff-candidate'
                                            value={candidate.staffGU ?? ''}
                                            checked={chosenStaffGU() === candidate.staffGU}
                                            onChange={() => setChosenStaffGU(candidate.staffGU ?? '')}
                                        />
                                        {/*
                                          * formattedName is display-only ("Last, First"). It is never what
                                          * gets written to the row — the server writes the chosen
                                          * candidate's own FirstName / LastName separately, because
                                          * LastName is half of a bound two-column FormattedName and a
                                          * pre-joined value renders as "Doe, Jane, " on the roster.
                                          */}
                                        <span style='font-size:1.05em;'>
                                            {candidate.formattedName
                                                || [candidate.lastName, candidate.firstName].filter(Boolean).join(', ')
                                                || 'Unnamed employee'}
                                        </span>
                                    </label>
                                )}</For>
                            </div>
                            <div style='display:flex; justify-content:center; width:100%;'>
                                <Button
                                    icon='fas fa-user-check'
                                    type='primary'
                                    enabled={!!chosenStaffGU() && !isProcessing()}
                                    loading={isProcessing()}
                                    onClick={() => void completeStaffCheckIn()}
                                >
                                    {isProcessing() ? 'Completing...' : 'Complete Check-In (No Badge)'}
                                </Button>
                            </div>
                        </Match>
                    </Switch>
                </div>
            </Show>

            {/*
              * VISITOR (non-staff) routing — behaviourally unchanged. isStaffRow() is false for every
              * walk-in and for every screening-flagged visitor row, so this renders exactly as before.
              */}
            <Show when={!isStaffRow()}>
            <Switch>
                <Match when={checkin.verificationStatus() === 'verified' && checkin.repeatTier() === 'Grace'}>
                    <div style="display:flex; flex-direction:column; align-items:center; gap:8px; padding:24px;">
                        <i class="fas fa-circle-check" style="font-size:2.5em; color: var(--st-success-color);" />
                        <span style="font-size:1.4em; font-weight:bold;">You're still checked in — welcome back</span>
                        <span style="opacity:0.8;">No new badge needed. Your visit has been re-activated.</span>
                    </div>
                </Match>
                <Match when={checkin.verificationStatus() === 'verified' && !offender()}>
                    <span role="presentation" class={styles['verified']}>
                        <svg width="128" height="128" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle
                                class={`${styles['draw-path']} ${styles['circle']}`}
                                pathLength={1}
                                cx="24"
                                cy="24"
                                r="20"
                                fill="transparent"
                                stroke-width="4"
                                stroke="currentColor"
                            />
                            <path
                                class={`${styles['draw-path']} ${styles['check']}`}
                                pathLength={1}
                                d="M16 24 L22 30 L32 20"
                                stroke="currentColor"
                                stroke-width="4"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                    {checkin.verificationStatusMessage()}
                </Match>
                <Match when={checkin.verificationStatus() === 'dangerous'}>
                    {(() => {
                        const entry = checkin.blockedMatches()[0];
                        const disp = (entry?.disposition || 'BLOCK').toUpperCase();
                        const bs = bannerStyle(entry?.color);
                        return (
                            <div class={styles['danger-banner']} style={`background: ${bs.bg}; border-color: ${bs.border}; color: ${bs.fg};`}>
                                    <i class={bs.icon}/>
                                    <Show
                                        when={checkin.blockedMatches().length > 0}
                                        fallback={
                                            <div style='display: flex; flex-direction: column; align-items: center; font-size: 0.7em;'>
                                                <span style='font-size: 1em; font-weight: bold;'>The scanned ID was flagged as a potential match with a known offender.</span>
                                                <div style='font-size: 0.8em; font-style: italic; opacity: 0.9;'>
                                                    Possible match determined based on First Name, Last Name, and Date of Birth.
                                                </div>
                                            </div>
                                        }
                                    >
                                        <span style='font-size: 1.6em; font-weight: bold; text-align: center;'>
                                            {disp === 'NOTIFY' ? (entry?.reasonDescription || 'High-Profile Visitor') : 'Visitor Blocked'}
                                        </span>
                                    </Show>
                                    <i class={bs.icon}/>
                            </div>
                        );
                    })()}
                </Match>
                <Match when={checkin.verificationStatus() === 'review'}>
                    <div class={styles['danger-banner']}>
                            <i class={`fas fa-exclamation-triangle`}/>
                            <div style='display: flex; flex-direction: column; align-items: center; font-size: 0.7em;'>
                                <span style='font-size: 1em; font-weight: bold;'>{checkin.verificationStatusMessage()}</span>
                                <div style='font-size: 0.8em; font-style: italic; opacity: 0.9;'>
                                    Please review the potential match below and confirm or dismiss.
                                </div>
                            </div>
                            <i class={`fas fa-exclamation-triangle`}/>
                    </div>
                </Match>
                {/* While retrying from the 'unavailable' state the retry panel below carries its own
                    progress feedback, so suppress this loader rather than showing both. */}
                <Match when={(checkin.verificationStatus() === 'pending' || checkin.isVerifying()) && reviewRoute() !== 'unavailable'}>
                    <Loader size='large'/>
                    {checkin.verificationStatusMessage()}
                </Match>
            </Switch>
            </Show>

            <Show when={checkin.offenders().length > 0}>
                <div class={styles['offender-comparison']}>
                    <div class={styles['comparison-panel']}>
                        <span class={styles['comparison-panel-title']}>Scanned Info</span>

                        <div class={styles['comparison-top-panel']}>
                            <div class={styles['comparison-image']}>
                                <Show when={checkin.scan()?.photoBase64}
                                      fallback={<div class={styles['comparison-photo-placeholder']}>No photo</div>}>
                                    <img src={`data:image/bmp;base64,${checkin.scan()!.photoBase64}`}></img>
                                </Show>
                            </div>
                            <div class={styles['comparison-info']}>
                                <span class={styles['comparison-name']}>{formatName(checkin.scan()?.AAMVA)}</span>
                                <Field label="Date of Birth" value={formatDob(checkin.scan()?.AAMVA?.dateOfBirth)} />
                                <Field label="Sex" value={formatSex(checkin.scan()?.AAMVA?.sex)} />
                                <Field label="Hair Color" value={offender()?.hairColor} />
                                <Field label="Eye Color" value={offender()?.eyeColor} />
                                <Field label="Height" value={formatHeight(offender()?.height)} />
                                <Field label="Address" value={formatAddress(offender())} />
                            </div>
                        </div>

                        <div class={styles['drivers-license']}>
                            <Show when={checkin.scan()?.idBase64}
                                  fallback={<div class={styles['comparison-photo-placeholder']}>No ID image</div>}>
                                <img style={{
                                    'aspect-ratio': '3.375/2.125',
                                    'border-radius': '8px',
                                    height: '200px',
                                }} src={`data:image/bmp;base64,${checkin.scan()!.idBase64}`}></img>
                            </Show>
                        </div>
                    </div>

                    <div class={styles['comparison-panel']}>
                        <span class={styles['comparison-panel-title']}>Potential Match</span>
                        <div class={styles['comparison-top-panel']}>
                            <div class={styles['comparison-image']}>
                                <img src={offender()?.offenderImageUrl} />
                            </div>

                            <div class={styles['comparison-info']}>
                                <span class={styles['comparison-name']}>{offender()?.name}</span>

                                <Field label="Date of Birth" value={formatDob(offender()?.dob)} />
                                <Field label="Sex" value={formatSex(offender()?.sex)} />
                                <Field label="Hair Color" value={offender()?.hairColor} />
                                <Field label="Eye Color" value={offender()?.eyeColor} />
                                <Field label="Height" value={formatHeight(offender()?.height)} />
                                <Field label="Address" value={formatAddress(offender())} />
                            </div>

                        </div>

                        <div class={styles['offender-actions']}>
                            {/* False Alarm claims the row and advances to Print; in review mode it
                                writes NOTHING here — CompleteCheckin happens once, in submit(), so
                                staff edits on the Print step are what get persisted. Abandoning at
                                Print therefore leaves the row REVW and still resolvable. See
                                writeApprovalIfNotReview above. */}
                            <Button icon='fas fa-bell-slash' type='success' onClick={rejectMatch}>False Alarm</Button>
                            <Button icon='fas fa-siren-on' type='danger' onClick={confirmMatch}>Confirm Match – Deny & Show Script</Button>
                        </div>
                    </div>
                </div>
            </Show>

            <Show when={checkin.blockedMatches().length > 0}>
                <div class={styles['offender-comparison']}>
                    <div class={styles['comparison-panel']}>
                        <span class={styles['comparison-panel-title']}>Scanned Info</span>

                        <div class={styles['comparison-top-panel']}>
                            <div class={styles['comparison-image']}>
                                <Show when={checkin.scan()?.photoBase64}
                                      fallback={<div class={styles['comparison-photo-placeholder']}>No photo</div>}>
                                    <img src={`data:image/bmp;base64,${checkin.scan()!.photoBase64}`}></img>
                                </Show>
                            </div>
                            <div class={styles['comparison-info']}>
                                <span class={styles['comparison-name']}>{formatName(checkin.scan()?.AAMVA)}</span>
                                <Field label="Date of Birth" value={formatDob(checkin.scan()?.AAMVA?.dateOfBirth)} />
                                <Field label="Sex" value={formatSex(checkin.scan()?.AAMVA?.sex)} />
                            </div>
                        </div>

                        <div class={styles['drivers-license']}>
                            <Show when={checkin.scan()?.idBase64}
                                  fallback={<div class={styles['comparison-photo-placeholder']}>No ID image</div>}>
                                <img style={{
                                    'aspect-ratio': '3.375/2.125',
                                    'border-radius': '8px',
                                    height: '200px',
                                }} src={`data:image/bmp;base64,${checkin.scan()!.idBase64}`}></img>
                            </Show>
                        </div>
                    </div>

                    <div class={styles['comparison-panel']}>
                        <span class={styles['comparison-panel-title']}>Blocked List Match</span>
                        <div class={styles['comparison-top-panel']}>
                            <div class={styles['comparison-info']}>
                                <span class={styles['comparison-name']}>{checkin.blockedMatches()[0]?.firstName} {checkin.blockedMatches()[0]?.lastName}</span>
                                <Field label="Date of Birth" value={formatDob(checkin.blockedMatches()[0]?.dateOfBirth)} />
                                <Field label="Reason" value={checkin.blockedMatches()[0]?.reasonDescription || checkin.blockedMatches()[0]?.reasonCode} />
                                <Field label="Notes" value={checkin.blockedMatches()[0]?.notes} />
                            </div>
                        </div>

                        <div class={styles['offender-actions']}>
                            {(() => {
                                const entry = checkin.blockedMatches()[0];
                                const disp = (entry?.disposition || 'BLOCK').toUpperCase();
                                return (
                                    <Show
                                        when={entry?.tier === 'Block'}
                                        fallback={<>
                                            <Button icon='fas fa-ban' type='secondary' onClick={notAMatch}>Not a Match</Button>
                                            <Show
                                                when={disp === 'NOTIFY'}
                                                fallback={<Button icon='fas fa-siren-on' type='danger' onClick={confirmBlocked}>Confirm Match – Deny & Show Script</Button>}
                                            >
                                                <Button type='success' icon='fas fa-right-to-bracket' onClick={allowAndNotify}>Allow + Notify Staff</Button>
                                            </Show>
                                        </>}
                                    >
                                        <Show
                                            when={disp === 'NOTIFY'}
                                            fallback={<Button icon='fas fa-siren-on' type='danger' onClick={confirmBlocked}>Visitor Denied - Show District Script</Button>}
                                        >
                                            <Button type='success' icon='fas fa-right-to-bracket' onClick={allowAndNotify}>Allow + Notify Staff</Button>
                                        </Show>
                                    </Show>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </Show>

            {/*
              * Route 'unavailable': screening could not be re-derived. A real state, not a toast —
              * the step is never marked completed and verificationStatus stays 'error', so Next
              * stays disabled and the Print step is unreachable until a retry succeeds. Retry
              * re-invokes runReviewScreening in place, so hydrated context and any staff edits to
              * type / reason / meeting-with survive. No retry limit, no automatic retry.
              */}
            <Show when={reviewRoute() === 'unavailable'}>
                <div class={styles['screening-unavailable']}>
                    <i class='fas fa-triangle-exclamation' />
                    <p>Screening is unavailable right now. This visitor cannot be cleared until it succeeds.</p>
                    <Button icon='fas fa-rotate-right' type='primary' enabled={!rerunning()} loading={rerunning()} onClick={() => void runReviewScreening()}>
                        {rerunning() ? 'Retrying...' : 'Retry screening'}
                    </Button>
                </div>
            </Show>

            {/* !isStaffRow() is belt-and-braces: a staff row holds verificationStatus at 'pending', so
                this is already hidden. Stated explicitly because "add this district employee to the
                Blocked / No-Fly list" is not a resolution this panel should ever offer for a badge
                that merely matched two people. */}
            <Show when={!isStaffRow() && reviewRoute() !== 'unavailable' && checkin.verificationStatus() !== 'pending' && !checkin.isVerifying() && checkin.blockedMatches().length === 0}>
                <div style='display: flex; justify-content: center; width: 100%; margin-top: 8px;'>
                    <Button icon='fas fa-ban' type='secondary' onClick={openNoFlyModal}>Add to Blocked / No-Fly List</Button>
                </div>
            </Show>

            <Show when={denialScript() !== null}>
                <DenialScriptOverlay script={denialScript()!} onDone={finishAndClose} />
            </Show>

            <Show when={showNoFlyModal()}>
                <div style='position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;'>
                    <div style='background: var(--st-page-bg); border-radius: 8px; padding: 24px; min-width: 360px; max-width: 480px; display: flex; flex-direction: column; gap: 16px; box-shadow: 0 4px 32px rgba(0,0,0,0.3);'>
                        <span style='font-size: 1.1em; font-weight: bold;'>Add to Blocked / No-Fly List</span>

                        <div style='display: flex; flex-direction: column; gap: 4px;'>
                            <label style='font-size: 0.85em;'>Reason</label>
                            <select
                                style='padding: 6px 8px; border-radius: 4px; border: 1px solid var(--st-panel-border); background: var(--st-well-bg); color: var(--st-page-fg);'
                                value={noFlyReason()}
                                onchange={(e) => setNoFlyReason(e.currentTarget.value)}
                            >
                                {NO_FLY_REASONS.map(r => (
                                    <option value={r.code}>{r.label}</option>
                                ))}
                            </select>
                        </div>

                        <div style='display: flex; flex-direction: column; gap: 4px;'>
                            <label style='font-size: 0.85em;'>Notes</label>
                            <textarea
                                style='padding: 6px 8px; border-radius: 4px; border: 1px solid var(--st-panel-border); background: var(--st-well-bg); color: var(--st-page-fg); resize: vertical; min-height: 80px;'
                                value={noFlyNotes()}
                                onInput={(e) => setNoFlyNotes(e.currentTarget.value)}
                                placeholder='Optional notes...'
                            />
                        </div>

                        <div style='display: flex; align-items: center; gap: 8px;'>
                            <input
                                type='checkbox'
                                id='nf-district-wide'
                                checked={noFlyDistrictWide()}
                                onChange={(e) => setNoFlyDistrictWide(e.currentTarget.checked)}
                            />
                            <label for='nf-district-wide' style='font-size: 0.9em;'>District-Wide</label>
                        </div>

                        <div style='display: flex; justify-content: flex-end; gap: 8px;'>
                            <Button type='secondary' onClick={() => setShowNoFlyModal(false)}>Cancel</Button>
                            <Button icon='fas fa-ban' type='danger' onClick={confirmAddToNoFly}>
                                {isAddingToNoFly() ? 'Adding...' : 'Add to No-Fly List'}
                            </Button>
                        </div>
                    </div>
                </div>
            </Show>
        </div>
    )
}

const Field = (props: { label: string, value: JSX.Element | string | undefined }) => {
    return (
        <div class={styles['field']}>
            <span class={styles['label']}>{props.label}:</span>
            <span class={styles['value']}>{props.value ?? 'N/A'}</span>
        </div>
    )
}

